import type ParcelTemplate from '~/types/ParcelTemplate';

import { distanceUnitMap } from '~/constants/distance';
import { weightUnitMap } from '~/constants/weight';
import { type PackageTemplateChooserFormValues } from '~/pages/common/PackageTemplateChooser/types';
import {
  fromWebCarrierParcelTemplate,
  type WebCarrierParcelTemplate,
} from '~/types/CarrierParcelTemplate';
import { type RecipientType, type ShipmentExtras } from '~/types/OrderBatchEntry';
import {
  type DimensionProvider,
  type JSONParcelTemplate,
  type SavedJSONParcelTemplate,
  type WebUserParcelTemplate,
} from '~/types/ParcelTemplate';
import { fromWebUserParcelTemplate, isParcelTemplate } from '~/types/ParcelTemplate';
import SignatureConfirmation from '~/types/SignatureConfirmation';
import { newRelic } from '~/utils/newRelic/newRelic';

import type CarrierParcelTemplate from './CarrierParcelTemplate';

import { type JSONCarrierParcelTemplate } from './CarrierParcelTemplate';
import {
  fromJSON as fromJSONCarrierParcelTemplate,
  toJSON as toJSONCarrierParcelTemplate,
} from './CarrierParcelTemplate';
import {
  fromJSON as fromJSONParcelTemplate,
  toJSON as toJSONParcelTemplate,
} from './ParcelTemplate';

export type Parcel = {
  carrierParcelTemplate: Maybe<CarrierParcelTemplate>;
  distanceUnit: string;
  height: string;
  length: string;
  useImportedOrderWeight?: boolean;
  userParcelTemplate: Maybe<ParcelTemplate>;
  weight: string;
  weightUnit: string;
  width: string;
};

export type WebParcel = {
  carrier_parcel_template?: WebCarrierParcelTemplate;
  distance_unit: string;
  height: string;
  length: string;
  mass_unit: string;
  object_id?: string;
  use_item_weight: boolean;
  user_parcel_template?: WebUserParcelTemplate;
  weight: string;
  width: string;
};

interface BaseJSONParcel {
  carrier_parcel_template: Maybe<JSONCarrierParcelTemplate>;
  distance_unit: string;
  height: string;
  length: string;
  mass_unit: string;
  use_imported_order_weight?: boolean;
  weight: string;
  width: string;
}

export interface JSONParcel extends BaseJSONParcel {
  user_parcel_template: Maybe<JSONParcelTemplate>;
}

export interface SavedJSONParcel extends BaseJSONParcel {
  user_parcel_template: Maybe<SavedJSONParcelTemplate>;
}

export const fromJSON = (jsonParcel: JSONParcel): Parcel => {
  let weight = jsonParcel.weight;
  try {
    weight = Number(weight).toFixed(2);
  } catch (error) {
    newRelic.noticeError('Failed to parse parcel weight', { error, weight });
  }
  return {
    carrierParcelTemplate:
      jsonParcel.carrier_parcel_template &&
      fromJSONCarrierParcelTemplate(jsonParcel.carrier_parcel_template),
    distanceUnit: jsonParcel.distance_unit,
    height: jsonParcel.height,
    length: jsonParcel.length,
    userParcelTemplate:
      jsonParcel.user_parcel_template && fromJSONParcelTemplate(jsonParcel.user_parcel_template),
    weight,
    weightUnit: jsonParcel.mass_unit,
    width: jsonParcel.width,
  };
};

export const toJSON = (parcel: Parcel): SavedJSONParcel => ({
  carrier_parcel_template:
    parcel.carrierParcelTemplate && toJSONCarrierParcelTemplate(parcel.carrierParcelTemplate),
  distance_unit: parcel.distanceUnit,
  height: parcel.height,
  length: parcel.length,
  mass_unit: parcel.weightUnit,
  use_imported_order_weight: parcel.useImportedOrderWeight,
  user_parcel_template:
    parcel.userParcelTemplate && toJSONParcelTemplate(parcel.userParcelTemplate),
  weight: parcel.weight,
  width: parcel.width,
});

export const fromWebParcel = (webParcel: WebParcel): Parcel => {
  const userParcelTemplate = fromWebUserParcelTemplate(webParcel.user_parcel_template);
  const carrierParcelTemplate = fromWebCarrierParcelTemplate(webParcel.carrier_parcel_template);

  return {
    carrierParcelTemplate,
    distanceUnit: webParcel.distance_unit,
    height: webParcel.height,
    length: webParcel.length,
    useImportedOrderWeight: webParcel.use_item_weight,
    userParcelTemplate,
    weight: webParcel.weight,

    weightUnit: webParcel.mass_unit,
    width: webParcel.width,
  };
};

export const getParcelData = (data: PackageTemplateChooserFormValues): Parcel => {
  // Logic copied from OrdersBatch
  if (
    data.extras.isUsingCustomDimensions &&
    data.customDimensions &&
    !data.extras.carrierTemplateWithCustomDimensions
  ) {
    return {
      carrierParcelTemplate: null,
      distanceUnit: data.customDimensions.distanceUnit.value.toString(),
      height: data.customDimensions.height,
      length: data.customDimensions.length,
      useImportedOrderWeight: data.useImportedOrderWeight,
      userParcelTemplate: null,
      weight: data.weight,
      weightUnit: data.weightUnit.value.toString(),
      width: data.customDimensions.width,
    };
  }

  const parcelTemplate = data.parcelTemplate;
  // if it's a user parcel template, get the dimensions from the template
  if (parcelTemplate && isParcelTemplate(parcelTemplate)) {
    // if it's derived from a carrier template, the dimensions are from the nested template
    if (parcelTemplate.carrierTemplate) {
      const carrierTemplate = parcelTemplate.carrierTemplate;

      return {
        carrierParcelTemplate: null,
        distanceUnit: distanceUnitMap[carrierTemplate.distanceUnit],
        height: carrierTemplate.height.toString(),
        length: carrierTemplate.length.toString(),
        useImportedOrderWeight: data.useImportedOrderWeight,
        userParcelTemplate:
          data.parcelTemplate && isParcelTemplate(data.parcelTemplate) ? data.parcelTemplate : null,
        weight: data.weight,
        weightUnit: weightUnitMap[String(data.weightUnit.value)],
        width: carrierTemplate.width.toString(),
      };
    }

    // otherwise the dimensions are from the parcel template
    return {
      carrierParcelTemplate: null,
      distanceUnit: distanceUnitMap[parcelTemplate.distanceUnit],
      height: parcelTemplate.height.toString(),
      length: parcelTemplate.length.toString(),
      useImportedOrderWeight: data.useImportedOrderWeight,
      userParcelTemplate:
        data.parcelTemplate && isParcelTemplate(data.parcelTemplate) ? data.parcelTemplate : null,
      weight: data.weight,
      weightUnit: weightUnitMap[String(data.weightUnit.value)],
      width: parcelTemplate.width.toString(),
    };
  }

  if (data.extras.carrierTemplateWithCustomDimensions && data.customDimensions) {
    return {
      carrierParcelTemplate: parcelTemplate,
      distanceUnit: data.customDimensions.distanceUnit.value.toString(),
      height: data.customDimensions.height.toString() || '0',
      length: data.customDimensions.length.toString(),
      useImportedOrderWeight: data.useImportedOrderWeight,
      userParcelTemplate: null,
      weight: data.weight,
      weightUnit: weightUnitMap[String(data.weightUnit.value)],
      width: data.customDimensions.width.toString(),
    };
  }

  // otherwise this is a carrier template
  return {
    carrierParcelTemplate: parcelTemplate,
    distanceUnit: distanceUnitMap[parcelTemplate!.distanceUnit],
    height: parcelTemplate!.height.toString(),
    length: parcelTemplate!.length.toString(),
    useImportedOrderWeight: data.useImportedOrderWeight,
    userParcelTemplate: null,
    weight: data.weight,
    weightUnit: weightUnitMap[String(data.weightUnit.value)],
    width: parcelTemplate!.width.toString(),
  };
};

export const getExtrasData = (
  data: PackageTemplateChooserFormValues,
): Pick<
  ShipmentExtras,
  'alcohol' | 'dryIce' | 'hazmat' | 'isReturnRequested' | 'signatureConfirmation'
> => ({
  alcohol: data.alcohol.containsAlcohol
    ? {
        containsAlcohol: data.alcohol.containsAlcohol,
        recipientType: data.alcohol.recipientType.value as RecipientType,
      }
    : null,
  dryIce: data.dryIce.containsDryIce
    ? {
        containsDryIce: data.dryIce.containsDryIce,
        weight: data.dryIce.weight,
        weightUnitId: data.dryIce.weightUnitId,
      }
    : null,
  hazmat: data.hazmat
    ? {
        isBiologicalMaterial: data.hazmat.containsBiologicalMaterial,
        isHazmat: data.hazmat.containsHazmat,
        isLithiumBatteries: data.hazmat.containsLithiumBatteries,
      }
    : undefined,
  isReturnRequested: data.isReturnRequested,
  signatureConfirmation: data.alcohol.containsAlcohol
    ? {
        hasSignatureConfirmation: true,
        signatureConfirmationType: SignatureConfirmation.ADULT,
      }
    : undefined,
});

export const toParcelTemplate = (parcel: Parcel): ParcelTemplate => {
  const templateBase = {
    weight: parseFloat(parcel.weight),
    weightUnit: weightUnitMap[parcel.weightUnit],
  };

  if (parcel.userParcelTemplate) {
    return {
      ...parcel.userParcelTemplate,
      distanceUnit: distanceUnitMap[parcel.distanceUnit],
      height: parseFloat(parcel.height),
      length: parseFloat(parcel.length),
      width: parseFloat(parcel.width),
      ...templateBase,
    };
  }

  // Custom dimensions, or carrier template if parcel has it
  return {
    carrierTemplate: parcel.carrierParcelTemplate,
    distanceUnit: distanceUnitMap[parcel.distanceUnit],
    height: parseFloat(parcel.height),
    isDefault: false,
    length: parseFloat(parcel.length),
    name: '',
    width: parseFloat(parcel.width),
    ...templateBase,
  };
};

export const fromParcelTemplate = (parcelTemplate: ParcelTemplate): Parcel => ({
  carrierParcelTemplate: null,
  distanceUnit: parcelTemplate.distanceUnit,
  height: parcelTemplate.height.toString(),
  length: parcelTemplate.length.toString(),
  userParcelTemplate: parcelTemplate,
  weight: parcelTemplate.weight.toString(),
  weightUnit: parcelTemplate.weightUnit,
  width: parcelTemplate.width.toString(),
});

/**
 * Get the name of the template used to create a parcel, with one exception: if a user parcel template was
 * modified after it was used to create the parcel, and they no longer match, treat the parcel as if it had custom
 * dimensions rather than a template
 */
export const getTemplateName = (parcel: {
  carrierParcelTemplate?: { name: string } | null;
  distanceUnit: string;
  height: number | string;
  length: number | string;
  userParcelTemplate?:
    | ({ carrierTemplate?: { name: string } | null; name: string } & DimensionProvider)
    | null;
  width: number | string;
}): Maybe<string> =>
  parcel.userParcelTemplate?.carrierTemplate?.name ||
  (matchesTemplate(parcel, parcel.userParcelTemplate) && parcel.userParcelTemplate?.name) ||
  parcel.carrierParcelTemplate?.name ||
  null;

/**
 * Determines if a parcel's dimensions match a user template. Used to identify cases where a template was
 * applied to create a parcel, but then the template was modified and no longer matches the parcel
 */
export const matchesTemplate = (
  parcel: {
    distanceUnit: string;
    height: number | string;
    length: number | string;
    userParcelTemplate?: { carrierTemplate?: { name: string } | null; name: string } | null;
    width: number | string;
  },
  template: DimensionProvider | null | undefined,
): boolean =>
  parseFloat(`${parcel.length}`) === template?.length &&
  parseFloat(`${parcel.width}`) === template?.width &&
  parseFloat(`${parcel.height}`) === template?.height &&
  parcel.distanceUnit === template?.distanceUnit?.toString();

type ParcelRequestDimensions = {
  distance_unit: string;
  height: string;
  length: string;
  width: string;
};

export type ParcelRequest = {
  mass_unit: string;
  use_imported_order_weight: boolean;
  weight: string;
} & (
  | ({
      carrier_parcel_template_token: string;
    } & Partial<ParcelRequestDimensions>)
  | ({
      carrier_parcel_template_token?: never;
      user_parcel_template_object_id?: never;
    } & ParcelRequestDimensions)
  | {
      user_parcel_template_object_id: string;
    }
);

export default Parcel;
